import template from "!!raw-loader!./MiniAdHocFilterContainer.html";
import Spec from "csi/core/Spec";
import html from "csi/html";
import locale from "csi/locale";
import { getRuleSpecValueString, RuleSpec } from "csi/middleware/attributeRule/RuleService";
import MultiFilterCondition from "csi/toolbar/MultiFilterCondition";
import NameValuePair from "csi/widget/NameValuePair";
import Button from "dijit/form/Button";
import _Container from "dijit/_Container";
import _TemplatedMixin from "dijit/_TemplatedMixin";
import _WidgetBase from "dijit/_WidgetBase";
import declare from "dojo/_base/declare";
import _ from "lodash";

const CSI_SELECTED = "csi-selected";

const MiniAdHocFilterContainer = declare([_WidgetBase, _TemplatedMixin, _Container], {
	templateString: template,

	/** @property {RightMenuAccordionItem} accordionItem - parent widget */
	accordionItem: null,

	/** @property {DOMNode} containerNode - the container for the tab view */
	containerNode: null,

	/** @property {boolean} showClearFiltersButton  - hide / show clear all filters button */
	showClearFiltersButton: false,

	/**
	 * Clear all filters.
	 */
	clearFilters() {
		const { accordionFilter } = this.accordionItem;
		accordionFilter.setFilterMap({});
		accordionFilter.resetFilterPanel();
		accordionFilter.onClickApply(null, true);
	},

	/**
	 * Click on filter.
	 */
	clickFilter(spec: Spec) {
		this.accordionItem.accordionFilter.selectFilterAttribute(spec);
	},

	/**
	 * Click X in filter.
	 */
	removeFilter(spec: Spec) {
		const { accordionFilter } = this.accordionItem;
		const extendedKey = spec.key.toString();
		accordionFilter.removeKeysFromFilterMap([extendedKey]);
		accordionFilter.resetFilterPanel();
		accordionFilter.onClickApply(null, true);
	},

	/**
	 * Add filter pill.
	 */
	addFilter(ruleSpec: RuleSpec, value: string[]) {
		const { spec } = ruleSpec;
		const { name } = spec;

		const title = new MultiFilterCondition({
			label: name,
			value,
		});

		this.addChild(new NameValuePair({
			iconCss: "material-icons csi-name-value-pair-close",
			iconText: "close",
			iconTitle: locale.resource("Remove"),
			id: spec.key.toString(),
			name,
			spec,
			title,
			titlePosition: ["below-centered", "above-centered"],
			value: getRuleSpecValueString(ruleSpec),
			onClick: (): void => this.clickFilter(spec),
			onIcon: (): void => this.removeFilter(spec),
		}));
	},

	/**
	 * @override
	 * Hide container on this lifecycle.
	 */
	postCreate() {
		html.setVisible(this.containerNode, false);
		this.inherited(arguments);
	},

	/**
	 * Select filter and de-select all others.
	 */
	selectFilter(spec: Spec) {
		const filterPills = this.containerNode.querySelectorAll(".csi-name-value-pair");
		const specExtendedKey = spec.key.toString();
		filterPills.forEach((pill) => {
			pill.classList.toggle(CSI_SELECTED, pill.id === specExtendedKey);
		});
	},

	/**
	 * Reset filters at specific index with current Map settings.
	 * If selectLastIndex is true, select the last filter pill.
	 */
	renderFilters(selectLastIndex?: boolean) {
		const { accordionItem, containerNode, showClearFiltersButton } = this;
		const { filterMap } = accordionItem.accordionFilter;
		html.clear(containerNode, true);
		const specMapLength = _.size(filterMap);
		if (!specMapLength) {
			html.setVisible(containerNode, false);
			return;
		}
		html.setVisible(containerNode, true);

		for (const spec in filterMap) {
			const ruleSpec = filterMap[spec];
			this.addFilter(ruleSpec, ruleSpec.rule.values);
		}

		if (selectLastIndex) {
			const filterMapKey = Object.keys(filterMap)[specMapLength - 1];
			this.selectFilter(filterMap[filterMapKey].spec);
		}

		if (showClearFiltersButton) {
			// render action to clear all filters
			this.addChild(new Button({
				class: html.CSS_BUTTON_LINK,
				// force wrap of this item
				style: "flex-basis: 100%",
				label: locale.resource("ClearAll"),
				onClick: (): boolean => {
					this.clearFilters();
					return true;
				},
			}));
		}
	},
});

export = MiniAdHocFilterContainer;